import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import {
  NotificationService,
  SendNotificationRequest,
  NOTIFICATION_CHANNELS,
  channelLabel,
} from '../../services/notification';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    TextareaModule,
  ],
  templateUrl: './notifications.html',
  styleUrls: ['./notifications.css'],
})
export class Notifications implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(NotificationService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  readonly channels = NOTIFICATION_CHANNELS;
  readonly channelLabel = channelLabel;

  loading = signal(false);
  sending = signal(false);
  showModal = signal(false);
  items = signal<any[]>([]);

  form: FormGroup = this.fb.group({
    channel: [1, Validators.required],
    recipient: ['', Validators.required],
    subject: [''],
    body: ['', Validators.required],
    scheduledAt: [''],
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.items.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openSend() {
    this.form.reset({ channel: 1, recipient: '', subject: '', body: '', scheduledAt: '' });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  send() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Channel, recipient and message are required');
      return;
    }
    this.sending.set(true);
    const v = this.form.value;
    const payload: SendNotificationRequest = {
      channel: Number(v.channel),
      recipient: v.recipient,
      subject: v.subject || null,
      body: v.body,
      scheduledAt: v.scheduledAt ? new Date(v.scheduledAt).toISOString() : null,
    };

    this.svc.send(payload).subscribe({
      next: (res: any) => {
        this.sending.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to send notification');
          return;
        }
        this.toast.success(payload.scheduledAt ? 'Notification scheduled' : 'Notification sent');
        this.closeModal();
        this.load();
      },
      error: () => this.sending.set(false),
    });
  }

  retry(item: any) {
    const id = item?.id ?? item?.notificationId;
    if (id == null) return;
    this.svc.retry(Number(id)).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Retry failed');
          return;
        }
        this.toast.success('Notification retried');
        this.load();
      },
    });
  }

  statusSeverity(status: string): 'success' | 'danger' | 'warn' | 'secondary' {
    const s = (status || '').toLowerCase();
    if (s.includes('sent') || s.includes('success') || s.includes('deliver')) return 'success';
    if (s.includes('fail') || s.includes('error')) return 'danger';
    if (s.includes('pending') || s.includes('queue') || s.includes('schedul')) return 'warn';
    return 'secondary';
  }
}
